import * as HoverCard from '@radix-ui/react-hover-card';
import styled, { css, keyframes } from 'styled-components';

import {
  BasePanel,
  Button as SharedButton,
  Checkbox as CheckboxIcon,
  CheckboxChecked as CheckboxCheckedIcon,
} from 'styles/components';
import {
  Colors,
  ColorScheme,
  Fonts,
  getScrollbarStyle,
} from 'styles/constants';

export { SettingsIcon } from 'styles/components';

export const MainPanel = styled(BasePanel)`
  padding: 32px;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const ContentCard = styled.main`
  width: 100%;
  min-height: calc(100vh - 205px);
  overflow: hidden;
  border: 1px solid ${Colors.Gray30};
  border-radius: 8px;
  background: ${Colors.White};
  box-shadow: 0 2px 8px rgba(46, 46, 54, 0.08);
`;

export const TabContent = styled.section`
  padding: 24px;
  overflow: auto;
  ${getScrollbarStyle()};
`;

export const CellCenteredContent = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ChecklistStepCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${ColorScheme.Text};
  text-transform: uppercase;
`;

export const ChecklistStepIcon = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  color: ${({ $checked }) => ($checked ? Colors.Green : ColorScheme.Text)};
`;

export const StatusDot = styled.span<{ color: string }>`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ color }) => color};
`;

export const StatusCheckboxGroup = styled.fieldset`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  min-width: 0;
  min-height: 32px;
  margin: 0;
  border: 0;
  padding: 0;
`;

export const StatusCheckboxLegend = styled.legend`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  clip-path: inset(50%);
`;

export const StatusCheckboxOption = styled.label`
  position: relative;
  display: inline-flex;
  align-items: center;
  color: ${ColorScheme.Text};
  font-family: ${Fonts.GilroyRegular};
  font-size: 14px;
  cursor: pointer;
`;

export const StatusCheckboxIcons = styled.span.attrs({
  className: 'icons',
})`
  display: inline-flex;
  margin-right: 8px;
  border-radius: 4px;
`;

export const StatusCheckboxInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;

  &:checked + ${StatusCheckboxIcons} > .checked {
    display: initial;
  }

  &:not(:checked) + ${StatusCheckboxIcons} > .unchecked {
    display: initial;
  }

  &:focus-visible + ${StatusCheckboxIcons} {
    outline: 2px solid ${Colors.Orange};
    outline-offset: 2px;
  }
`;

export const StatusCheckedIcon = styled(CheckboxCheckedIcon).attrs({
  'size': 24,
  'className': 'checked',
  'aria-hidden': true,
})`
  display: none;
`;

export const StatusUncheckedIcon = styled(CheckboxIcon).attrs({
  'size': 24,
  'className': 'unchecked',
  'aria-hidden': true,
})`
  display: none;
  color: ${Colors.Gray50};
`;

const physicalDockPulse = keyframes`
  0%, 100% {
    color: ${Colors.Gray50};
    border-color: ${Colors.Gray40};
    background: ${Colors.Gray10};
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
    transform: scale(1);
  }

  50% {
    color: ${Colors.Gray70};
    border-color: ${Colors.Gray50};
    background: ${Colors.White};
    box-shadow: 0 1px 4px rgba(71, 85, 105, 0.25),
      0 0 8px rgba(100, 116, 139, 0.2);
    transform: scale(1.05);
  }
`;

export const PhysicalDockBadge = styled.span<{ $highlighted: boolean }>`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  border: 1px solid ${Colors.Gray40};
  border-radius: 6px;
  background: ${Colors.Gray10};
  color: ${Colors.Gray50};
  transform-origin: center;

  ${({ $highlighted }) =>
    $highlighted &&
    css`
      animation: ${physicalDockPulse} 2s ease-in-out infinite;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const OrderTypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 10px;
  background: #eef1f5;
  color: #344054;
  font-family: ${Fonts.GilroyBold};
  font-size: 14px;
  line-height: 1;
  text-transform: uppercase;
`;

export const StatusLegendOrderTypeBadge = styled(OrderTypeBadge)`
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  font-size: 11px;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const Label = styled.label`
  color: ${ColorScheme.Text};
  font-family: ${Fonts.GilroySemiBold};
  font-size: 14px;
`;

const fieldStyle = `
  box-sizing: border-box;
  width: 100%;
  height: 38px;
  border: 1px solid ${Colors.Gray40};
  border-radius: 6px;
  padding: 0 12px;
  background: ${Colors.White};
  color: ${ColorScheme.Text};
  font-family: ${Fonts.GilroyRegular};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${Colors.Orange};
    box-shadow: 0 0 0 1px ${Colors.Orange};
  }
`;

export const Input = styled.input`
  ${fieldStyle};
`;

export const FileInput = styled(Input)`
  padding: 0 12px 0 0;
  line-height: 36px;

  &::file-selector-button {
    height: 100%;
    margin: 0 10px 0 0;
    border: 0;
    border-right: 1px solid ${Colors.Gray40};
    padding: 0 12px;
    background: ${Colors.White};
    color: ${ColorScheme.Text};
    font: inherit;
    vertical-align: middle;
    cursor: pointer;
  }
`;

export const Textarea = styled.textarea`
  ${fieldStyle};
  height: auto;
  min-height: 80px;
  padding: 8px 12px;
  resize: vertical;
`;

export const EntryForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const FieldError = styled.span`
  color: ${Colors.Magenta};
  font-family: ${Fonts.GilroyRegular};
  font-size: 12px;
`;

export const FormActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
`;

export const Button = styled(SharedButton).attrs({ size: 'small' })``;

export const TableScroller = styled.div`
  width: 100%;
  overflow-x: auto;
  ${getScrollbarStyle()};
`;

export const Table = styled.table`
  width: 100%;
  min-width: 1180px;
  border-collapse: collapse;
  color: ${ColorScheme.Text};
  font-size: 14px;

  th,
  td {
    padding: 14px 12px;
    border-bottom: 1px solid ${Colors.Gray30};
    text-align: left;
    vertical-align: middle;
  }

  th {
    background: ${Colors.Gray10};
    font-family: ${Fonts.GilroySemiBold};
    white-space: nowrap;
  }

  td {
    font-family: ${Fonts.GilroyRegular};
  }
`;

export const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${Colors.Orange};
    outline-offset: 2px;
  }
`;

export const StatusCell = styled.td`
  font-family: ${Fonts.GilroySemiBold} !important;
`;

export const EmptyCell = styled.td`
  padding: 32px !important;
  color: ${ColorScheme.LightText};
  text-align: center !important;
`;

export const ClampText = styled.span`
  display: block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ActionCell = styled.td`
  position: relative;
  width: 1%;
  white-space: nowrap;
`;

export const ActionMenu = styled.div`
  position: absolute;
  z-index: 1;
  top: calc(100% - 8px);
  right: 12px;
  min-width: 170px;
  overflow: hidden;
  border: 1px solid ${Colors.Gray30};
  border-radius: 6px;
  background: ${Colors.White};
  box-shadow: 0 4px 12px ${Colors.Gray40};
`;

export const MenuAction = styled.button`
  display: block;
  width: 100%;
  border: 0;
  padding: 10px 12px;
  background: ${Colors.White};
  color: ${ColorScheme.Text};
  font-family: ${Fonts.GilroyRegular};
  font-size: 14px;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${Colors.Gray10};
  }

  &:disabled {
    color: ${ColorScheme.LightText};
    cursor: not-allowed;
  }
`;

export const PaginationSlot = styled.div`
  padding-top: 8px;
`;

export const StatusLegendContent = styled(HoverCard.Content)`
  z-index: 50;
  min-width: 180px;
  border: 1px solid ${Colors.Gray30};
  border-radius: 8px;
  padding: 8px;
  background: ${Colors.White};
  box-shadow: 0 4px 12px rgba(46, 46, 54, 0.16);
`;

export const StatusLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  color: ${ColorScheme.Text};
  font-family: ${Fonts.GilroyRegular};
  font-size: 14px;
`;
